"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

// Tam ekran modal arka planı: yazıldığı yerde değil, <body>'nin sonunda render
// edilir.
//
// Neden portal: `fixed` bir katman, ancak sayfanın kök yığın bağlamında
// (stacking context) durduğu sürece her şeyin üstünde kalır. Modallarımızın
// birkaçı ise kendi yığın bağlamını yaratan bir düğümün içinde tanımlı,
// AppShell'deki `sticky` kenar çubuğu bunlardan biri. İç içe bir bağlamda hiçbir
// z-index, katmanı belgede daha sonra boyanan öğelerin üstüne çıkaramaz:
// `relative` bir sarmalayıcı (vaka kartındaki "Geri bildirim" rozeti) ya da
// `opacity` ile soluklaştırılmış bir düğme (ayarlardaki pasif "Kaydet") tarayıcı
// tarafından konumlandırılmış öğeler katmanına terfi ettirilir ve modalın
// üstünde çizilir.
//
// Portal bu hata sınıfını tümden kaldırır: katman her zaman en son boyanır.
// className/onClick/aria-* gibi tüm div nitelikleri olduğu gibi geçirilir, bu
// yüzden çağıran taraf yalnızca dış <div>'ini bu bileşenle değiştirir.
//
// Erişilebilirlik (F25): katman açıkken <body>'nin diğer çocukları `inert`
// olur (Tab ve ekran okuyucu arkaya geçemez), odak modalın içindeki ilk
// odaklanabilir öğeye taşınır ve kapanınca açan öğeye döner; `onEscape`
// verildiyse Escape onu çağırır. Zaten inert olan kardeşlere dokunulmaz, iç içe
// açılan ikinci katman ilkini bozmaz.
type Props = React.ComponentProps<"div"> & { onEscape?: () => void };

const FOCUSABLE = '[autofocus], button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ModalOverlay({ onEscape, ...props }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const siblings = Array.from(document.body.children).filter(
      (el) => el !== node && !el.hasAttribute("inert"),
    );
    siblings.forEach((el) => el.setAttribute("inert", ""));
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = node.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node).focus({ preventScroll: true });
    return () => {
      siblings.forEach((el) => el.removeAttribute("inert"));
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    if (!onEscape) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { e.stopPropagation(); onEscape!(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onEscape]);

  // Portal hedefi yalnızca tarayıcıda var. Gecikmeli bir "mounted" bayrağı
  // yerine doğrudan document'e bakıyoruz ki içerik ilk render'da bassın:
  // modalların içindeki ref'ler (iyzico form kabı, konfeti canvas'ı) ilk effect
  // turunda dolu olmak zorunda.
  if (typeof document === "undefined") return null;
  return createPortal(<div ref={ref} tabIndex={-1} {...props} />, document.body);
}
